import React from "react";
// ANT DESIGN COMPONENTS
import { Dropdown, Space } from "antd";
import { useNavigate } from "react-router-dom";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faTrash,
  faKey,
  faLock,
  faUnlock,
  faBan,
  faShield,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
import { updateOpenModalTotpToken } from "../../../redux/authentication/authenticationSlice";
import {
  updateOpenModalDeleteUser,
  updateOpenModalResetUserPassword,
  updateOpenModalBlockUnBlockUser,
  updatePswType,
  updateUser,
} from "../../../redux/users/userSlice";
// UTILS
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// INTERFACES
import { RootState } from "../../../app/store";
import { UserStatus } from "../../../flatbuffers/generated/archive/user-status";
import { formatUuidT } from "../../../flatbuffers/display";

interface UsersTableActionsProps {
  /** The user row these actions act on. */
  selectedItem: any;
  /**
   * Legacy prop kept for callers that still hold the flag locally. The value
   * also goes into the users slice, which is what UserCrudSurface reads.
   */
  setPswType?: (value: string) => void;
  /**
   * Overrides the default "navigate to the Organization Management edit page"
   * behaviour. The wizard passes its own handler so Edit opens in place rather
   * than throwing the operator out of the wizard mid-setup.
   */
  onEdit?: (item: any) => void;
}

function UsersTableActions({
  selectedItem,
  setPswType = undefined,
  onEdit = undefined,
}: UsersTableActionsProps) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleOpenModalEdit = () => {
    if (onEdit) {
      onEdit(selectedItem);
      return;
    }

    const id =
      typeof selectedItem?.id === "object"
        ? formatUuidT(selectedItem?.id)
        : String(selectedItem?.id ?? "");
    if (id) {
      navigate(`/organization-management/users/edit/${id}`);
    }
  };

  const handleOpenModalDelete = () => {
    dispatch(updateUser(selectedItem));
    dispatch(updateOpenModalDeleteUser(true));
  };

  const handleOpenModalResetUserPassword = () => {
    dispatch(updateUser(selectedItem));
    dispatch(updateOpenModalResetUserPassword(true));
  };

  const handleOpenModalLockUnlock = () => {
    dispatch(updateUser(selectedItem));
    dispatch(updateOpenModalBlockUnBlockUser(true));
  };

  const handleGetOfflinePassword = () => {
    setPswType?.("get");
    dispatch(updatePswType("get"));
    dispatch(updateUser(selectedItem));
    dispatch(updateOpenModalTotpToken(true));
  };

  const handleResetOfflinePassword = () => {
    setPswType?.("post");
    dispatch(updatePswType("post"));
    dispatch(updateUser(selectedItem));
    dispatch(updateOpenModalTotpToken(true));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const BLANK = {
      label: (
        <div className="generalStyles__tableActionBox">
          <span>
            <FontAwesomeIcon className="generalStyles__error generalStyles__mrFix" icon={faBan} />
            {t("general.noActions")}
          </span>
        </div>
      ),
      key: "0",
    };

    const EDIT = hasPermission(Permission.UsersUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faEdit}
              text={t("organizationManagement.edit")}
              onClick={handleOpenModalEdit}
            />
          ),
          key: "1",
        }
      : null;

    const GET_OFFLINE_PASSWORD =
      hasPermission(Permission.UsersOfflinePswUserView, permissionSet ?? []) &&
      selectedItem &&
      selectedItem.createdOfflinePsw
        ? {
            label: (
              <TableActionsMenuItem
                icon={faShield}
                text={t("userManagement.getOfflinePassword")}
                onClick={handleGetOfflinePassword}
              />
            ),
            key: "2",
          }
        : null;

    const RESET_OFFLINE_PASSWORD = hasPermission(
      Permission.UsersOfflinePswUserCreate,
      permissionSet ?? [],
    )
      ? {
          label: (
            <TableActionsMenuItem
              icon={faShield}
              text={t("userManagement.generateNewPassword")}
              onClick={handleResetOfflinePassword}
            />
          ),
          key: "3",
        }
      : null;

    const BLOCK_USER = hasPermission(Permission.UsersBlockUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faLock}
              text={t("general.block")}
              onClick={handleOpenModalLockUnlock}
            />
          ),
          key: "4",
        }
      : null;

    const UNLOCK_USER = hasPermission(Permission.UsersUnblockUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faUnlock}
              text={t("general.unlock")}
              onClick={handleOpenModalLockUnlock}
            />
          ),
          key: "5",
        }
      : null;

    let BLOCK_UNLOCK_USER = null;
    if (selectedItem.status === UserStatus.ACTIVE) {
      BLOCK_UNLOCK_USER = BLOCK_USER;
    } else {
      BLOCK_UNLOCK_USER = UNLOCK_USER;
    }

    const RESET_PASSWORD = hasPermission(Permission.UsersResetUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faKey}
              text={t("userManagement.resetPassword")}
              onClick={handleOpenModalResetUserPassword}
            />
          ),
          key: "6",
        }
      : null;

    const DELETE = hasPermission(Permission.UsersDelete, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faTrash}
              text={t("userManagement.delete")}
              onClick={handleOpenModalDelete}
            />
          ),
          key: "7",
        }
      : null;
    // RETURN OBJECT WITH VALID ACTIONS
    //
    // OFFLINE_ACTIVE belongs in this branch, not its own: it is what
    // OFFLINE_CREATED becomes the moment the user replaces their generated
    // password with one of their own, and an admin's ability to manage the
    // account -- edit it, reset ITS password again, delete it -- does not
    // stop being needed just because that one milestone happened. Before
    // this, an offline user who had ever completed their own password
    // change fell through every status branch below to BLANK: no edit, no
    // re-reset, no delete, permanently, for an account otherwise perfectly
    // normal and fully permissioned.
    if (
      selectedItem.status === UserStatus.ACTIVE ||
      selectedItem.status === UserStatus.OFFLINE_CREATED ||
      selectedItem.status === UserStatus.OFFLINE_ACTIVE
    ) {
      if (
        EDIT ||
        BLOCK_UNLOCK_USER ||
        RESET_PASSWORD ||
        RESET_OFFLINE_PASSWORD ||
        GET_OFFLINE_PASSWORD ||
        DELETE
      ) {
        return [
          EDIT,
          BLOCK_UNLOCK_USER,
          RESET_PASSWORD,
          RESET_OFFLINE_PASSWORD,
          GET_OFFLINE_PASSWORD,
          DELETE,
        ];
      }
    }
    if (selectedItem.status === UserStatus.NEW) {
      if (EDIT || DELETE) {
        return [EDIT, DELETE];
      }
    }
    if (selectedItem.status === UserStatus.BLOCKED) {
      if (BLOCK_UNLOCK_USER || DELETE) {
        return [BLOCK_UNLOCK_USER, DELETE];
      }
    }
    return [BLANK];
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Dropdown menu={{ items: manageActions() }} trigger={["click"]}>
        <Space className="generalStyles__hoverCursor">
          <FontAwesomeIcon icon={faEllipsis} />
        </Space>
      </Dropdown>
    </div>
  );
}

export default UsersTableActions;
